import React, { useState, useEffect } from 'react';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import { signIn, signUp, confirmSignUp, resetPassword, confirmResetPassword, getCurrentUser } from 'aws-amplify/auth';
import BowtieIcon from './BowtieIcon';
import Tooltip from './Tooltip';
import styles from './CustomAuth.module.css';

interface CustomAuthenticatorProps {
  children: (props: { signOut?: () => void; user?: any; isAuthenticated: boolean }) => React.ReactElement;
  showModal: boolean;
  onModalClose: () => void;
  defaultTab?: 'signIn' | 'signUp';
}

// Custom Auth Form Component
const CustomAuthForm: React.FC<{ defaultTab?: 'signIn' | 'signUp'; onAuthSuccess?: () => void }> = ({ defaultTab = 'signIn', onAuthSuccess }) => {
  const [activeTab, setActiveTab] = useState<'signIn' | 'signUp'>(defaultTab);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Update activeTab when defaultTab changes
  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const result = await signIn({ username: email, password });
      
      // Force a re-check of authentication status
      if (onAuthSuccess) {
        onAuthSuccess();
      }
      
      // Clear form
      setEmail('');
      setPassword('');
    } catch (err: any) {
      console.error('Sign in error:', err);
      setError(err.message || 'An error occurred during sign in');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }
    
    try {
      const result = await signUp({ username: email, password });
      
      // Handle confirmation step if needed
      if (onAuthSuccess) {
        onAuthSuccess();
      }
      
      // Clear form
      setEmail('');
      setPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      console.error('Sign up error:', err);
      setError(err.message || 'An error occurred during sign up');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address first');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await resetPassword({ username: email });
      setError('Password reset instructions sent to your email');
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.customAuthForm}>
      {/* Tabs */}
      <div className={styles.authTabs}>
        <Tooltip content="Switch to sign in">
          <button
            className={`${styles.authTab} ${activeTab === 'signIn' ? styles.active : ''}`}
            onClick={() => setActiveTab('signIn')}
            type="button"
            aria-label="Switch to sign in tab"
          >
            Sign in
          </button>
        </Tooltip>
        <Tooltip content="Switch to create account">
          <button
            className={`${styles.authTab} ${activeTab === 'signUp' ? styles.active : ''}`}
            onClick={() => setActiveTab('signUp')}
            type="button"
            aria-label="Switch to create account tab"
          >
            Create Account
          </button>
        </Tooltip>
      </div>

      {/* Beta Notice - Only show on Sign Up tab */}
      {activeTab === 'signUp' && (
        <div className={styles.betaNotice}>
          <div className={styles.betaNoticeContent}>
            <div className={styles.betaNoticeTitle}>
              <span className={styles.betaNoticeIcon}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L13.09 8.26L19 7L15.46 11.46L20 16L13.74 14.91L12 21L10.26 14.91L4 16L8.54 11.46L5 7L10.91 8.26L12 2Z" fill="currentColor"/>
                </svg>
              </span>
              Private Beta
            </div>
            <div className={styles.betaNoticeText}>
              Handsome CV is currently in a private beta and under active development. For inquiries or to request a private demonstration, please contact{' '}
              <a href="mailto:john.termaat@gmail.com">john.termaat@gmail.com</a>.
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={activeTab === 'signIn' ? handleSignIn : handleSignUp}>
        {error && (
          <div className={styles.authError}>
            {error}
          </div>
        )}

        {/* Email Field */}
        <div className={styles.authField}>
          <div className={styles.authFieldRow}>
            <label htmlFor="email" className={styles.authLabel}>Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={styles.authInput}
              required
            />
          </div>
        </div>

        {/* Password Field */}
        <div className={styles.authField}>
          <div className={styles.authFieldRow}>
            <label htmlFor="password" className={styles.authLabel}>Password</label>
            <div className={styles.authPasswordContainer}>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={styles.authInput}
                required
              />
              <Tooltip content={showPassword ? 'Hide password' : 'Show password'}>
                <button
                  type="button"
                  className={styles.authPasswordToggle}
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M1 1l22 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  )}
                </button>
              </Tooltip>
            </div>
          </div>
        </div>

        {/* Confirm Password Field (only for sign up) */}
        {activeTab === 'signUp' && (
          <div className={styles.authField}>
            <div className={styles.authFieldRow}>
              <label htmlFor="confirmPassword" className={styles.authLabel}>Confirm Password</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={styles.authInput}
                required
              />
            </div>
          </div>
        )}

        {/* Submit Button */}
        <Tooltip content={loading ? 'Processing...' : (activeTab === 'signIn' ? 'Sign in to your account' : 'Create a new account')}>
          <button
            type="submit"
            className={styles.authSubmitButton}
            disabled={loading}
            aria-label={activeTab === 'signIn' ? 'Sign in' : 'Create Account'}
          >
            {loading ? 'Loading...' : (activeTab === 'signIn' ? 'Sign in' : 'Create Account')}
          </button>
        </Tooltip>

        {/* Forgot Password Link (only for sign in) */}
        {activeTab === 'signIn' && (
          <Tooltip content="Reset your password">
            <button
              type="button"
              className={styles.authForgotPassword}
              onClick={handleForgotPassword}
              disabled={loading}
              aria-label="Forgot password"
            >
              Forgot password?
            </button>
          </Tooltip>
        )}
      </form>
    </div>
  );
};

// Inner component that uses the useAuthenticator hook
const AuthenticatorContent: React.FC<CustomAuthenticatorProps> = ({ children, showModal, onModalClose, defaultTab }) => {
  const { user, signOut } = useAuthenticator((context) => [context.user, context.signOut]);
  const [authState, setAuthState] = useState<{ user: any; isAuthenticated: boolean }>({ user: null, isAuthenticated: false });
  
  // Update local auth state when user changes
  useEffect(() => {
    setAuthState({ user, isAuthenticated: !!user });
  }, [user]);
  
  // Initial authentication check on mount
  useEffect(() => {
    const checkInitialAuth = async () => {
      try {
        const currentUser = await getCurrentUser();
        setAuthState({ user: currentUser, isAuthenticated: !!currentUser });
      } catch (error) {
        setAuthState({ user: null, isAuthenticated: false });
      }
    };
    
    checkInitialAuth();
  }, []);
  
  // Function to force refresh authentication state
  const refreshAuthState = async () => {
    try {
      // Add a small delay to ensure Amplify state has updated
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const currentUser = await getCurrentUser();
      setAuthState({ user: currentUser, isAuthenticated: !!currentUser });
    } catch (error) {
      setAuthState({ user: null, isAuthenticated: false });
    }
  };
  
  const isAuthenticated = authState.isAuthenticated;

  // Wrap signOut to update local state immediately
  const handleSignOut = async () => {
    try {
      // Update local state immediately for instant UI feedback
      setAuthState({ user: null, isAuthenticated: false });
      
      // Then call the actual signOut function
      if (signOut) {
        await signOut();
      }
      
    } catch (error) {
      console.error('Sign out error:', error);
      // If sign out fails, we should refresh the auth state
      refreshAuthState();
    }
  };

  // Handle modal backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onModalClose();
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showModal) {
        onModalClose();
      }
    };

    if (showModal) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [showModal, onModalClose]);

  // If user just authenticated and modal is open, close it
  useEffect(() => {
    if (isAuthenticated && showModal) {
      onModalClose();
    }
  }, [isAuthenticated, showModal, onModalClose]);

  return (
    <>
      {/* Always render the main app content */}
      {children({ 
        signOut: handleSignOut, 
        user: authState.user, 
        isAuthenticated 
      })}

      {/* Modal overlay for authentication */}
      {showModal && !isAuthenticated && (
        <div className={styles.authModalOverlay} onClick={handleBackdropClick}>
          <div className={styles.authModalBackdrop} />
          <div className={styles.authModalContainer}>
            <Tooltip content="Close" position="left">
              <button 
                className={styles.authModalClose}
                onClick={onModalClose}
                aria-label="Close authentication modal"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </Tooltip>
            
            <div className={styles.authModalContent}>
              <div className={styles.authHeader}>
                <div className={styles.authBrand}>
                  <div className={styles.authBrandIcon}>
                    <BowtieIcon width={40} height={40} />
                  </div>
                  <span className={styles.authBrandText}>handsome CV</span>
                </div>
                <h1 className={styles.authTitle}>Welcome!</h1>
                <p className={styles.authSubtitle}>Sign in and let's tell your story.</p>
              </div>

              <div className={styles.authFormContainer}>
                <CustomAuthForm defaultTab={defaultTab} onAuthSuccess={refreshAuthState} />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Main CustomAuthenticator component
const CustomAuthenticator: React.FC<CustomAuthenticatorProps> = (props) => {
  return (
    <Authenticator.Provider>
      <AuthenticatorContent {...props} />
    </Authenticator.Provider>
  );
};

export default CustomAuthenticator;
